<template>
  <div class="bookmark-panel">
    <div class="bookmark-header">
      <div><span class="nr-kicker">NOTES</span><h3>书签与划线</h3></div>
      <button class="panel-close" aria-label="关闭书签列表" @click="$emit('close')"><nricon name="close" /></button>
    </div>

    <div class="bookmark-content">
      <div v-if="!bookmarks.length" class="bm-empty">
        <nricon name="notebook-1" />
        <p>还没有书签或划线</p>
        <span class="hint">顶栏星标加书签，选中正文可划线</span>
      </div>

      <ul v-else class="note-list">
        <li
          v-for="bm in bookmarks"
          :key="bm.id"
          class="note-item"
          role="button"
          tabindex="0"
          @click="$emit('select', bm)"
          @keyup.enter="$emit('select', bm)"
        >
          <div class="n-main">
            <div v-if="bm.note" class="n-note">{{ bm.note }}</div>
            <div v-else class="n-note n-empty">（书签）</div>
            <div class="n-meta">
              <span v-if="bm.chapterTitle" class="n-chapter">{{ bm.chapterTitle }}</span>
              <span class="n-time">{{ formatTime(bm.createdAt) }}</span>
            </div>
          </div>
          <button class="n-del" title="删除" aria-label="删除书签或划线" @click.stop="$emit('delete', bm)"><nricon name="delete" /></button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BookmarkList',
  props: {
    bookmarks: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    // 与「我的笔记」页 Notes.vue 保持一致的时间格式
    formatTime(t) {
      if (!t) return ''
      const d = new Date(t)
      if (isNaN(d.getTime())) return ''
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  }
}
</script>

<style lang="scss" scoped>
.bookmark-panel {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 320px;
  background: var(--nr-surface);
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
}

.bookmark-header {
  padding: 18px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--nr-border);

  h3 { margin: 0; font-size: 16px; font-weight: 500; color: var(--nr-text); }
  i { cursor: pointer; font-size: 20px; color: var(--nr-text-2); }
}

.bookmark-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.bm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  color: var(--nr-text-3);
  i { font-size: 34px; }
  p { margin: 12px 0 4px; font-size: 14px; color: var(--nr-text-2); }
  .hint { font-size: 12px; color: var(--nr-text-3); }
}

/* 与 Notes.vue 的 .note-item 同款卡片，跟随主题 token */
.note-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.note-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: var(--nr-surface);
  border: 1px solid var(--nr-border);
  border-left: 3px solid var(--nr-accent);
  border-radius: var(--nr-radius);
  cursor: pointer;
  transition: box-shadow .15s;
  &:hover { box-shadow: var(--nr-shadow-hover); }

  .n-main { flex: 1; min-width: 0; }
  .n-note {
    font-size: 14px;
    color: var(--nr-text);
    line-height: 1.6;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    &.n-empty { color: var(--nr-text-3); font-style: italic; }
  }
  .n-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 6px;
    font-size: 12px;
    color: var(--nr-text-3);
  }
  .n-chapter {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 60%;
  }
  .n-del {
    flex-shrink: 0;
    border: none;
    background: transparent;
    color: var(--nr-text-3);
    cursor: pointer;
    padding: 2px 4px;
    font-size: 15px;
    &:hover { color: #f56c6c; }
  }
}
</style>

<style lang="scss" scoped>
.bookmark-panel {
  width: min(380px, 92vw);
  color: var(--nr-text);
  border-right: 1px solid var(--nr-border);
  background: var(--nr-surface);
  box-shadow: var(--nr-shadow-float);
}

.bookmark-header {
  min-height: 86px;
  padding: 18px 22px;
  h3 { margin-top: 4px; font-family: var(--nr-font-display); font-size: 23px; font-weight: 600; }
}

.panel-close {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  color: var(--nr-text-3);
  border: 0;
  border-radius: 50%;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  &:hover { color: var(--nr-text); background: var(--nr-surface-2); }
}

.bookmark-content { padding: 14px 16px 28px; }
.bm-empty {
  margin-top: 18px;
  padding: 54px 16px;
  border: 1px dashed var(--nr-border-strong);
  border-radius: var(--nr-radius-lg);
  i { display: grid; width: 58px; height: 58px; place-items: center; color: var(--nr-accent-text); border-radius: 50%; background: var(--nr-accent-bg); font-size: 24px; }
  p { margin-top: 16px; color: var(--nr-text); font-family: var(--nr-font-display); font-size: 17px; font-weight: 600; }
  .hint { margin-top: 3px; text-align: center; line-height: 1.6; }
}

.note-list { gap: 10px; }
.note-item {
  min-height: 122px;
  padding: 17px 15px 15px;
  border-left-width: 1px;
  border-radius: 14px;
  transition: border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
  &::before { content: '“'; color: var(--nr-accent); font-family: var(--nr-font-display); font-size: 27px; line-height: .9; }
  &:hover { border-color: var(--nr-border-strong); box-shadow: var(--nr-shadow); transform: translateY(-1px); }
  &:focus-visible { outline: 2px solid var(--nr-accent); outline-offset: 2px; }
  .n-note { font-family: var(--nr-font-display); font-size: 13px; line-height: 1.75; }
  .n-meta { margin-top: 11px; padding-top: 9px; border-top: 1px solid var(--nr-border); font-size: 10px; }
  .n-del { display: grid; width: 36px; height: 36px; place-items: center; border-radius: 50%; }
  .n-del:hover { color: var(--nr-danger); background: var(--nr-danger-bg); }
}

@media (max-width: 480px) {
  .bookmark-panel { width: 100%; }
  .bookmark-header { min-height: 76px; padding-top: max(14px, env(safe-area-inset-top)); }
  .note-item .n-del { width: 44px; height: 44px; }
}
</style>
